/* eslint-disable prettier/prettier */
/**
 * 图片相关工具方法
 */
import { dataURL2File, file2DataURL } from '@/pages/home/utils/file';

const createCompressError = (msg: string, error?: any): any => ({
  ...error,
  code: 'COMPRESS_ERROR',
  message: msg,
});

/**
 * 获取图像的Image对象数据
 * @param image Image 可以是dataURL、Image对象 也可以是 File对象
 * @returns Image 是 Image对象
 */
const getImage = (
  image: string | HTMLImageElement | File,
): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    if (image instanceof HTMLImageElement) {
      resolve(image);
      return;
    }

    (async () => {
      try {
        const dataURL =
          typeof image === 'string' ? image : await file2DataURL(image);
        const img = new Image();
        img.onload = () => {
          resolve(img);
        };
        img.onerror = (e) => {
          reject(createCompressError('getImage方法图片加载失败', e));
        };
        img.setAttribute('crossorigin', 'anonymous');
        img.setAttribute('src', dataURL);
      } catch (exception) {
        reject(createCompressError('getImage方法异常', exception));
      }
    })();
  });

/**
 * 图片压缩
 * @param image 待压缩的图片，可以是Image对象 或 File对象
 * @param fileName 生成的文件的文件名
 * @param quality 压缩的质量，0~1，数值越大，压缩后的图片清晰度越高
 * @returns 压缩后的图片，File对象
 */
export const compressImage = (
  imageFile: File,
  options?: {
    quality?: number;
    maxSize?: number;
    minQuality?: number;
  },
): Promise<{ file: File; dataURL: string }> =>
  new Promise((resolve, reject) => {
    if (!(imageFile instanceof File)) {
      reject(
        createCompressError(
          'compressImage方法异常: image参数不是File类型的实例',
        ),
      );
      return;
    }

    const quality = options?.quality || 0.8;
    const minQuality = options?.minQuality || 0.6;
    const maxSize = options?.maxSize || 6 * 1024 * 1024;

    // 执行压缩的方法，返回File类型实例
    const doCompress = async (
      imageFile: File,
      quality: number,
    ): Promise<File> => {
      if (imageFile?.size <= maxSize || quality <= minQuality)
        return Promise.resolve(imageFile);
      let image;
      try {
        image = await getImage(imageFile);
      } catch (exception) {
        return Promise.reject(exception);
      }
      const imageWidth = image?.width || 0;
      const imageHeight = image?.height || 0;
      const cvs = document.createElement('canvas');
      const ctx = cvs.getContext('2d');
      let dataURL = '';
      let compressedImageFile = null;
      cvs.width = imageWidth;
      cvs.height = imageHeight;
      ctx?.drawImage(image, 0, 0, imageWidth, imageHeight);
      dataURL = cvs.toDataURL('image/jpeg', quality);
      compressedImageFile = dataURL2File(dataURL, imageFile?.name);
      return doCompress(compressedImageFile, quality * 0.9);
    };

    (async () => {
      try {
        // 压缩图片
        const compressedImageFile = await doCompress(imageFile, quality);
        const compressedImageDataURL = await file2DataURL(compressedImageFile);
        resolve({
          file: compressedImageFile,
          dataURL: compressedImageDataURL,
        });
      } catch (exception) {
        reject(createCompressError('compressImage方法异常', exception));
      }
    })();
  });
